/**
 * Per-LINE state machine — docs/04-state-machine.md.
 *
 * Pure. Zero I/O, zero ambient time (a `clock` is always injected), zero randomness
 * (ids arrive as arguments). `transition()` RETURNS the events it would write; it never
 * persists them. See packages/returns-core/AGENTS.md.
 */
import type {
  Actor,
  ActorType,
  Disposition,
  Grade,
  LineState,
  LocalizedText,
  ReturnEvent,
  ReturnLine,
} from "@sirdab/returns-shared";

import { dispositionForGrade } from "./grade-disposition";

/* ------------------------------------------------------------------ *
 * Transition table
 * ------------------------------------------------------------------ */

/**
 * States a line can still move on from. Terminal (`restocked | quarantined | disposed`)
 * and exit (`rejected | lost_in_transit | expired`) states are absent: they are settled.
 */
export const TERMINAL_STATES = [
  "restocked",
  "quarantined",
  "disposed",
] as const;
export const EXIT_STATES = [
  "rejected",
  "cancelled",
  "lost_in_transit",
  "expired",
  // The merchant closed the return after review with no goods outcome and no refund.
  "completed",
] as const;

/**
 * States a shopper may still withdraw from: everything before the goods are physically in
 * motion. Once a carrier has the parcel, cancelling is meaningless — the box is coming back
 * either way, and the honest record is the parcel's, not the shopper's intention.
 */
export const CANCELLABLE_STATES = [
  "requested",
  "approved",
  "booking_failed",
  "awaiting_pickup",
] as const;

const CANCELLABLE: ReadonlySet<LineState> = new Set<LineState>(
  CANCELLABLE_STATES,
);

/** May the shopper (or the merchant on their behalf) still cancel this line? */
export const isCancellableState = (state: LineState): boolean =>
  CANCELLABLE.has(state);

const TERMINAL: ReadonlySet<LineState> = new Set<LineState>(TERMINAL_STATES);
const EXIT: ReadonlySet<LineState> = new Set<LineState>(EXIT_STATES);

export const isTerminalState = (state: LineState): boolean =>
  TERMINAL.has(state);
export const isExitState = (state: LineState): boolean => EXIT.has(state);
/** Settled = nothing further can happen to this line. */
export const isSettledState = (state: LineState): boolean =>
  isTerminalState(state) || isExitState(state);

export interface TransitionRule {
  readonly to: LineState;
  /** Actor types permitted to perform this transition ("Actor who exits it", docs/04). */
  readonly actors: readonly ActorType[];
}

/**
 * The complete legal transition table. Forward-only, with exactly two exceptions:
 * `requested -> rejected`, and any unsettled state -> `lost_in_transit`.
 * There is no un-receive and no un-grade — corrections are new events, not state rewinds.
 */
export const TRANSITIONS: Readonly<
  Record<LineState, readonly TransitionRule[]>
> = {
  requested: [
    { to: "approved", actors: ["merchant", "system"] },
    // The only backward-looking exception, and it requires a rejectionReason.
    { to: "rejected", actors: ["merchant", "system"] },
    // The shopper's own withdrawal. NOT a rejection: nobody refused them, so no reason is
    // required and none may be attached (docs/04, ReturnLineSchema).
    { to: "cancelled", actors: ["customer", "merchant"] },
    { to: "lost_in_transit", actors: ["carrier", "system"] },
  ],
  approved: [
    // Carrier accepts booking, AWB returned.
    { to: "awaiting_pickup", actors: ["system"] },
    // Booking failure is a recorded outcome, not an absence of one. Without this the only
    // trace of a failed attempt would be a missing AWB, which cannot tell "not tried yet"
    // apart from "tried twice and the carrier refused".
    { to: "booking_failed", actors: ["system"] },
    { to: "cancelled", actors: ["customer", "merchant"] },
    { to: "completed", actors: ["merchant"] },
    { to: "lost_in_transit", actors: ["carrier", "system"] },
  ],
  booking_failed: [
    // The retry path. A later attempt that succeeds moves the line on exactly as a first
    // one would — booking_failed is a stop on the way, not a dead end.
    { to: "awaiting_pickup", actors: ["system"] },
    // TODO(decision):Q7 — whether a permanently unbookable line falls back to drop-off or
    // is withdrawn depends on which carriers expose a reverse-pickup API at all. Both stay
    // open: no drop-off-specific state is invented here, and the shopper can still cancel.
    { to: "cancelled", actors: ["customer", "merchant"] },
    { to: "completed", actors: ["merchant"] },
    { to: "lost_in_transit", actors: ["carrier", "system"] },
  ],
  awaiting_pickup: [
    // Collection scan (pickup) or drop-off acceptance.
    { to: "in_transit", actors: ["carrier"] },
    // Timeout only — never a manual click. Driven by the injected clock.
    { to: "expired", actors: ["system"] },
    // Last moment: the parcel has not been handed over yet.
    { to: "cancelled", actors: ["customer", "merchant"] },
    { to: "completed", actors: ["merchant"] },
    { to: "lost_in_transit", actors: ["carrier", "system"] },
  ],
  in_transit: [
    // Dock scan.
    { to: "received", actors: ["warehouse"] },
    { to: "completed", actors: ["merchant"] },
    { to: "lost_in_transit", actors: ["carrier", "system"] },
  ],
  received: [
    // Grade recorded, line by line.
    { to: "inspected", actors: ["warehouse"] },
    { to: "completed", actors: ["merchant"] },
    { to: "lost_in_transit", actors: ["carrier", "system"] },
  ],
  inspected: [
    { to: "restocked", actors: ["warehouse"] },
    { to: "quarantined", actors: ["warehouse"] },
    // Requires recorded merchant consent BEFORE the fact (docs/04).
    { to: "disposed", actors: ["warehouse"] },
    { to: "completed", actors: ["merchant"] },
    { to: "lost_in_transit", actors: ["carrier", "system"] },
  ],
  // Settled states. TODO(decision):Q4 — `quarantined` has no auto-disposal timer and no
  // storage-cost owner yet, so it stays terminal here. TODO(decision):Q5 — `lost_in_transit`
  // has no claim workflow, so it stays an exit state with no onward transition.
  restocked: [],
  quarantined: [],
  disposed: [],
  rejected: [],
  // The shopper withdrew. Settled, and deliberately not re-openable: a change of mind after
  // cancelling is a new return, not a rewind of this one.
  cancelled: [],
  lost_in_transit: [],
  expired: [],
  // The merchant's own close-out. Settled and final: no refund is issued by this system.
  completed: [],
};

/* ------------------------------------------------------------------ *
 * Failure modes
 * ------------------------------------------------------------------ */

export type TransitionErrorCode =
  | "illegal_transition"
  | "wrong_actor"
  | "rejection_reason_required"
  | "merchant_consent_required"
  | "grade_required"
  | "disposition_mismatch"
  | "timeout_not_elapsed"
  | "booking_failure_required";

/** A transition attempted illegally fails loudly. Never a silent no-op. */
export class TransitionError extends Error {
  readonly code: TransitionErrorCode;
  readonly from: LineState;
  readonly to: LineState;
  readonly actorType: ActorType;

  constructor(
    code: TransitionErrorCode,
    message: string,
    context: { from: LineState; to: LineState; actorType: ActorType },
  ) {
    super(message);
    this.name = "TransitionError";
    this.code = code;
    this.from = context.from;
    this.to = context.to;
    this.actorType = context.actorType;
  }
}

/* ------------------------------------------------------------------ *
 * canTransition
 * ------------------------------------------------------------------ */

export type CanTransitionResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly code: "illegal_transition" | "wrong_actor" };

/**
 * Is `from -> to` legal at all, and is `actor` allowed to do it?
 * Payload requirements (rejection reason, merchant consent, grade) are checked by
 * `transition()`, not here — this answers "may this button be shown".
 */
export function canTransition(
  from: LineState,
  to: LineState,
  actor: ActorType | Actor,
): boolean {
  return checkTransition(from, to, actor).ok;
}

/** Same check as `canTransition`, but says why it failed. */
export function checkTransition(
  from: LineState,
  to: LineState,
  actor: ActorType | Actor,
): CanTransitionResult {
  const actorType = typeof actor === "string" ? actor : actor.type;
  const rule = (TRANSITIONS[from] ?? []).find(
    (candidate) => candidate.to === to,
  );
  if (rule === undefined) return { ok: false, code: "illegal_transition" };
  if (!rule.actors.includes(actorType))
    return { ok: false, code: "wrong_actor" };
  return { ok: true };
}

/** Every state reachable from `from` by `actor`. Useful for the merchant/warehouse UI. */
export function allowedTransitions(
  from: LineState,
  actor: ActorType | Actor,
): LineState[] {
  const actorType = typeof actor === "string" ? actor : actor.type;
  return (TRANSITIONS[from] ?? [])
    .filter((rule) => rule.actors.includes(actorType))
    .map((rule) => rule.to);
}

/* ------------------------------------------------------------------ *
 * transition
 * ------------------------------------------------------------------ */

/** Recorded BEFORE disposal, never after (docs/04). */
export interface MerchantConsent {
  readonly grantedBy: string;
  /** ISO-8601 UTC. Must not be later than the moment of disposal. */
  readonly grantedAt: string;
  readonly note?: string;
}

/**
 * A carrier booking attempt that did not produce an AWB.
 *
 * `reason` is an open string, not an enum: the adapter's vocabulary today is
 * `city_not_serviceable | carrier_error`, and TODO(decision):Q7 — which carriers expose a
 * reverse-pickup API at all — is unanswered, so nothing carrier-specific is modelled here.
 * `retryable` is the adapter's own flag, carried through verbatim.
 *
 * `attempt` is what makes "not booked yet" distinguishable from "booking failed twice":
 * the count lives on the append-only event, so a sweep can back off rather than hammer a
 * carrier that has already refused. The caller supplies it — core counts nothing.
 */
export interface BookingFailure {
  readonly reason: string;
  readonly retryable: boolean;
  /** 1 for the first failed attempt. */
  readonly attempt: number;
  readonly detail?: string;
}

export interface TransitionPayload {
  /** Id of the `return_event` to emit. Ids arrive as arguments — core never generates them. */
  readonly eventId: string;
  /** Injected clock. Core never calls `Date.now()` or bare `new Date()`. */
  readonly clock: () => Date;
  /** Mandatory when moving to `rejected`. Bilingual; renders on the tracking page. */
  readonly rejectionReason?: LocalizedText;
  /** Mandatory when moving to `inspected`. Drives the disposition mapping. */
  readonly grade?: Grade;
  /** Optional override of the mapped disposition; must match the target terminal state. */
  readonly disposition?: Disposition;
  /** What actually arrived. Never overwrites `qtyExpected` — the gap is the data (docs/03). */
  readonly qtyReceived?: number;
  /** Mandatory when moving to `disposed`. */
  readonly merchantConsent?: MerchantConsent;
  /** Mandatory when moving to `booking_failed`. What failed, and how many times. */
  readonly bookingFailure?: BookingFailure;
  /** Anything else worth keeping on the append-only event. */
  readonly meta?: Record<string, unknown>;
  /** Overrides the derived `event_type` (`created`, `approved`, ...). */
  readonly eventType?: string;
}

export interface TransitionResult {
  readonly line: ReturnLine;
  /** Every transition writes a `return_event`. No exceptions, no silent mutations. */
  readonly events: readonly ReturnEvent[];
}

const EVENT_TYPE_BY_STATE: Readonly<Record<LineState, string>> = {
  requested: "requested",
  approved: "approved",
  awaiting_pickup: "pickup_booked",
  in_transit: "collected",
  received: "received",
  inspected: "graded",
  booking_failed: "booking_failed",
  cancelled: "cancelled",
  restocked: "restocked",
  quarantined: "quarantined",
  disposed: "disposed",
  rejected: "rejected",
  lost_in_transit: "lost_in_transit",
  expired: "expired",
  completed: "completed",
};

const DISPOSITION_BY_TERMINAL: Partial<Record<LineState, Disposition>> = {
  restocked: "restock",
  quarantined: "quarantine",
  disposed: "dispose",
};

/**
 * Move one line forward. Pure: returns the new line plus the event(s) to append.
 * Throws `TransitionError` on anything illegal — wrong actor, backwards move,
 * rejection without a reason, disposal without recorded merchant consent.
 */
export function transition(
  line: ReturnLine,
  to: LineState,
  actor: Actor,
  payload: TransitionPayload,
): TransitionResult {
  const from = line.state;
  const context = { from, to, actorType: actor.type };

  const check = checkTransition(from, to, actor);
  if (!check.ok) {
    throw new TransitionError(
      check.code,
      check.code === "wrong_actor"
        ? `actor "${actor.type}" may not move a line from "${from}" to "${to}"`
        : `"${from}" -> "${to}" is not a legal transition`,
      context,
    );
  }

  const at = payload.clock().toISOString();

  // Rejection without a reason is structurally impossible. It renders to the shopper.
  if (to === "rejected" && payload.rejectionReason === undefined) {
    throw new TransitionError(
      "rejection_reason_required",
      "rejectionReason is mandatory when rejecting a line — it renders on the shopper tracking page",
      context,
    );
  }
  // A booking failure with nothing recorded about it is the dead end this state exists to
  // remove — an unexplained `booking_failed` is no better than a missing AWB.
  if (to === "booking_failed" && payload.bookingFailure === undefined) {
    throw new TransitionError(
      "booking_failure_required",
      "bookingFailure (reason, retryable, attempt) must be recorded when a carrier booking fails",
      context,
    );
  }
  if (to === "inspected" && payload.grade === undefined) {
    throw new TransitionError(
      "grade_required",
      "a grade must be recorded when a line is inspected",
      context,
    );
  }
  if (to === "disposed") {
    if (payload.merchantConsent === undefined) {
      throw new TransitionError(
        "merchant_consent_required",
        "disposal requires recorded merchant consent",
        context,
      );
    }
    // Consent must exist BEFORE the fact, not be back-filled afterwards. Compared as
    // instants: ISO strings with different precision do not sort as text, and an
    // unparseable timestamp is no evidence of consent at all.
    const grantedAtMs = Date.parse(payload.merchantConsent.grantedAt);
    if (Number.isNaN(grantedAtMs) || grantedAtMs > Date.parse(at)) {
      throw new TransitionError(
        "merchant_consent_required",
        "merchant consent must be recorded before disposal, not after",
        context,
      );
    }
  }

  // Disposition is derived from the grade at inspection — never a free choice.
  const mappedDisposition =
    DISPOSITION_BY_TERMINAL[to] ??
    (to === "inspected" && payload.grade !== undefined
      ? dispositionForGrade(payload.grade)
      : undefined);
  if (
    payload.disposition !== undefined &&
    mappedDisposition !== undefined &&
    payload.disposition !== mappedDisposition
  ) {
    throw new TransitionError(
      "disposition_mismatch",
      `disposition "${payload.disposition}" does not match terminal state "${to}"`,
      context,
    );
  }

  const next: ReturnLine = {
    ...line,
    state: to,
    grade: payload.grade ?? line.grade,
    disposition: mappedDisposition ?? payload.disposition ?? line.disposition,
    qtyReceived: payload.qtyReceived ?? line.qtyReceived,
    rejectionReason: payload.rejectionReason ?? line.rejectionReason,
    updatedAt: at,
  };

  const eventPayload: Record<string, unknown> = { ...(payload.meta ?? {}) };
  if (payload.grade !== undefined) eventPayload.grade = payload.grade;
  if (next.disposition !== null && mappedDisposition !== undefined) {
    eventPayload.disposition = next.disposition;
  }
  if (payload.qtyReceived !== undefined)
    eventPayload.qtyReceived = payload.qtyReceived;
  if (payload.rejectionReason !== undefined) {
    eventPayload.rejectionReason = payload.rejectionReason;
  }
  if (payload.merchantConsent !== undefined) {
    eventPayload.merchantConsent = payload.merchantConsent;
  }
  if (payload.bookingFailure !== undefined) {
    eventPayload.bookingFailure = payload.bookingFailure;
  }

  const event: ReturnEvent = {
    id: payload.eventId,
    returnOrderId: line.returnOrderId,
    returnLineId: line.id,
    actorType: actor.type,
    actorId: actor.id ?? null,
    fromState: from,
    toState: to,
    eventType: payload.eventType ?? EVENT_TYPE_BY_STATE[to],
    payload: eventPayload,
    createdAt: at,
  };

  return { line: next, events: [event] };
}

/* ------------------------------------------------------------------ *
 * Booking failure and its retry path
 * ------------------------------------------------------------------ */

/**
 * States a booking attempt may legitimately be recorded against: one nobody has tried yet,
 * and one the carrier has already refused at least once.
 */
export const BOOKING_FAILURE_RECORDABLE_STATES = [
  "approved",
  "booking_failed",
] as const;

const BOOKING_FAILURE_RECORDABLE: ReadonlySet<LineState> = new Set<LineState>(
  BOOKING_FAILURE_RECORDABLE_STATES,
);

/** May a carrier booking failure be recorded against a line in this state? */
export const canRecordBookingFailure = (state: LineState): boolean =>
  BOOKING_FAILURE_RECORDABLE.has(state);

/**
 * Record a failed carrier booking.
 *
 * A carrier booking answers `{ ok: false, reason, retryable }` rather than throwing,
 * because an unserviceable city is an ordinary answer. This is the state-machine
 * half of that: the failure lands somewhere nameable, with its attempt count, instead of the
 * line sitting at `approved` indistinguishable from one nobody has tried yet.
 *
 * Two shapes, because a first refusal and a repeat refusal are genuinely different things:
 *
 * - From `approved` this is a transition: the line moves to `booking_failed`.
 * - From `booking_failed` the line's state does not change — it is already there — so this
 *   is **not** a transition at all. It appends a further `booking_failed` event to the
 *   append-only log and returns the line with only `updatedAt` moved. `TRANSITIONS` stays
 *   forward-only: there is deliberately no `booking_failed -> booking_failed` self-loop,
 *   because inventing one would make `canTransition` claim a state change that does not
 *   happen and would open the door to re-entrancy as a general property of the machine.
 *   docs/04 requires that every transition writes an event; it never claimed every event is
 *   a transition, and "corrections are new events, not state rewinds" points the same way.
 *
 * Every attempt is therefore recorded: three refusals produce three events, carrying
 * `attempt` 1, 2 and 3, and the whole history survives a later successful booking.
 * The caller supplies `attempt` — core counts nothing.
 *
 * Retrying is an ordinary `transition(line, 'awaiting_pickup', ...)` from `booking_failed`.
 */
export function recordBookingFailure(
  line: ReturnLine,
  options: {
    readonly eventId: string;
    readonly clock: () => Date;
    readonly failure: BookingFailure;
    readonly actorId?: string | null;
    readonly meta?: Record<string, unknown>;
  },
): TransitionResult {
  const actor: Actor = { type: "system", id: options.actorId ?? null };

  if (line.state !== "booking_failed") {
    return transition(line, "booking_failed", actor, {
      eventId: options.eventId,
      clock: options.clock,
      bookingFailure: options.failure,
      ...(options.meta === undefined ? {} : { meta: options.meta }),
    });
  }

  const context = {
    from: line.state,
    to: "booking_failed" as const,
    actorType: actor.type,
  };
  // An unexplained repeat failure is no better than an unexplained first one. The type says
  // so, but the check is here too: the payload is mandatory on every recording, not just the
  // one that happens to travel through `transition()`.
  if ((options.failure as BookingFailure | undefined) === undefined) {
    throw new TransitionError(
      "booking_failure_required",
      "bookingFailure (reason, retryable, attempt) must be recorded when a carrier booking fails",
      context,
    );
  }

  const at = options.clock().toISOString();
  const event: ReturnEvent = {
    id: options.eventId,
    returnOrderId: line.returnOrderId,
    returnLineId: line.id,
    actorType: actor.type,
    actorId: actor.id ?? null,
    // from === to, and honestly so: nothing moved. The event records the attempt, not a move.
    fromState: "booking_failed",
    toState: "booking_failed",
    eventType: EVENT_TYPE_BY_STATE.booking_failed,
    payload: { ...(options.meta ?? {}), bookingFailure: options.failure },
    createdAt: at,
  };

  return { line: { ...line, updatedAt: at }, events: [event] };
}

/* ------------------------------------------------------------------ *
 * Shopper cancellation
 * ------------------------------------------------------------------ */

/**
 * The shopper withdraws their own return (or the merchant does it on their behalf).
 *
 * Distinct from `rejected` on purpose. `rejected` requires a bilingual `rejectionReason`
 * that renders on the tracking page, and every catalogue rejection string attributes the
 * refusal to the merchant — so recording a cancellation as a rejection tells a shopper who
 * cancelled that the store turned them down, and inflates the merchant's rejection rate in
 * their own analytics. Cancellation needs no reason because nobody refused anything.
 *
 * Legal only from `CANCELLABLE_STATES`, and only for `customer` or `merchant` — the
 * transition table enforces both, so an out-of-time cancel throws `illegal_transition` and
 * a `system` or `carrier` cancel throws `wrong_actor`.
 */
export function cancelLine(
  line: ReturnLine,
  actor: Actor,
  options: {
    readonly eventId: string;
    readonly clock: () => Date;
    /** Free-form, optional, and never shown as a refusal. Kept on the event only. */
    readonly note?: string;
  },
): TransitionResult {
  return transition(line, "cancelled", actor, {
    eventId: options.eventId,
    clock: options.clock,
    ...(options.note === undefined ? {} : { meta: { note: options.note } }),
  });
}

/* ------------------------------------------------------------------ *
 * awaiting_pickup timeout
 * ------------------------------------------------------------------ */

export interface PickupTimeoutOptions {
  readonly clock: () => Date;
  /**
   * How long a line may sit in `awaiting_pickup` before it expires.
   * No default is invented here — the window is merchant/carrier configuration and
   * is passed in by the caller.
   */
  readonly timeoutHours: number;
  /** When the line entered `awaiting_pickup`. Defaults to the line's `updatedAt`. */
  readonly enteredAt?: string;
}

const HOUR_MS = 60 * 60 * 1000;

/** Has this line sat in `awaiting_pickup` past its timeout, per the injected clock? */
export function isPickupExpired(
  line: ReturnLine,
  options: PickupTimeoutOptions,
): boolean {
  if (line.state !== "awaiting_pickup") return false;
  const enteredAt = Date.parse(options.enteredAt ?? line.updatedAt);
  const deadline = enteredAt + options.timeoutHours * HOUR_MS;
  return options.clock().getTime() >= deadline;
}

/**
 * Sweep helper: expire a line whose pickup window has elapsed. A shopper who never
 * hands over the parcel must not sit open forever. Throws if the timeout has not elapsed —
 * `expired` is never a manual click.
 */
export function expirePickup(
  line: ReturnLine,
  options: PickupTimeoutOptions & {
    readonly eventId: string;
    readonly actorId?: string | null;
  },
): TransitionResult {
  if (!isPickupExpired(line, options)) {
    throw new TransitionError(
      "timeout_not_elapsed",
      "the awaiting_pickup timeout has not elapsed — expiry is a timeout, not a manual action",
      { from: line.state, to: "expired", actorType: "system" },
    );
  }
  return transition(
    line,
    "expired",
    { type: "system", id: options.actorId ?? null },
    {
      eventId: options.eventId,
      clock: options.clock,
      meta: { timeoutHours: options.timeoutHours },
    },
  );
}
